import { SpinnerCentered } from "#web/components/common/SpinnerCentered/SpinnerCentered";
import { makeShop, ShopQuery } from "#web/components/shop/queries/shopQuery";
import { routeConfig } from "#web/configs/route";
import { api } from "#web/services/api";
import { i18nLike } from "#web/services/i18nLike";
import { useAction } from "@gadgetinc/react";
import {
  Badge,
  Banner,
  Bleed,
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  InlineGrid,
  InlineStack,
  List,
  Page,
  Text,
  TextField,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

const en = {
  activeFromToday: "Active from today",
  allCustomers: "All customers",
  amountOffOrder: "Amount off order",
  amountOffOrderDescription: "The discount will be applied to the entire order.",
  back: "Protected Links",
  banner: {
    description: "Note that the KeepCart Theme Extensions has to be enabled to use this feature.",
    title: "KeepCart Theme Extensions has to be enabled",
  },
  discard: "Discard",
  discountValue: "Discount Value",
  error: {
    description: "Please try again. If the issue persists, please contact support.",
    title: "Error loading page",
  },
  linkNotActive: "Link is not active yet.",
  performance: "Performance",
  save: "Save",
  secureLink: "Secure Link",
  subtitle: "Create a new link to distribute codes. Codes can only be applied when using the provided link.",
  summary: "Summary",
  title: "Create discount link",
  type: "Type",
};

export const LinkBlockingCreate = () => {
  const t = i18nLike(en);
  const navigate = useNavigate();
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;

  const [discountCode, setDiscountCode] = useState<string>("");
  const [value, setValue] = useState<string>("");

  const handleDiscontCodeChange = useCallback((newValue: string) => {
    setDiscountCode(newValue);
  }, []);
  const handleChange = useCallback((newValue: string) => {
    setValue(newValue);
  }, []);

  const [{ data, fetching }, create] = useAction(api.discountLinks.create);

  const handleSave = async () => {
    if (!shop) {
      return;
    }

    const r = await create({
      code: discountCode,
      shop: {
        _link: shop.id,
      },
      type: "percentage",
      value: parseFloat(value),
    });
    console.log("kc: data", data, r.data);
    navigate(routeConfig.linkblocking[":id"]["/"].replace(":id", r.data?.id ?? ""));
  };

  const handleDiscard = () => {
    navigate(routeConfig.linkblocking["/"]);
  };

  const PreviewCard = () => (
    <Card roundedAbove="sm">
      <BlockStack gap="400">
        <Text as="h6" fontWeight="semibold" variant="headingMd">
          {t("summary")}
        </Text>
        <Text as="h6" fontWeight="semibold" variant="headingMd">
          {`${shop?.domain ?? ""}/?code=${discountCode}`}
        </Text>

        <Box>
          <Bleed marginInline={{ sm: "500", xs: "400" }}>
            <Divider />
          </Bleed>
        </Box>
        <BlockStack gap="150">
          <Text as="h6" variant="headingSm">
            {t("type")}
          </Text>
          <List type="bullet">
            <List.Item>{t("amountOffOrder")}</List.Item>
            <List.Item>{t("secureLink")}</List.Item>
            <List.Item>{t("allCustomers")}</List.Item>
            <List.Item>{t("activeFromToday")}</List.Item>
          </List>
        </BlockStack>
        <BlockStack gap="150">
          <Text as="h6" variant="headingSm">
            {t("performance")}
          </Text>
          <Text as="p" tone="subdued">
            {t("linkNotActive")}
          </Text>
        </BlockStack>
      </BlockStack>
    </Card>
  );

  return (
    <Page
      backAction={{
        content: t("back"),
        onAction: () => {
          handleDiscard();
        },
      }}
      subtitle={t("subtitle")}
      title={t("title")}
      titleMetadata={<Badge tone="info">Beta</Badge>}
    >
      {shopError ? (
        <Banner title={t("error.title")} tone="critical">
          {t("error.description")}
        </Banner>
      ) : shopFetching ? (
        <SpinnerCentered />
      ) : (
        <InlineGrid columns={{ md: "2fr 1fr", xs: 1 }} gap="400">
          <BlockStack gap="400">
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <Banner title={t("banner.title")}>{t("banner.description")}</Banner>
                <BlockStack gap="100">
                  <Text as="h6" fontWeight="semibold" variant="headingMd">
                    {t("amountOffOrder")}
                  </Text>
                  <Text as="p" tone="subdued">
                    {t("amountOffOrderDescription")}
                  </Text>
                </BlockStack>
                <TextField
                  autoComplete="off"
                  label="Discount code"
                  onChange={handleDiscontCodeChange}
                  value={discountCode}
                />
                <TextField
                  autoComplete="off"
                  label="Link (can't be edited)"
                  value={`${shop?.domain ?? ""}/?code=${discountCode}`}
                />
              </BlockStack>
            </Card>
            <Card roundedAbove="sm">
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text as="h6" fontWeight="semibold" variant="headingMd">
                    {t("discountValue")}
                  </Text>
                </BlockStack>
                <TextField
                  autoComplete="off"
                  label="Percentage"
                  onChange={handleChange}
                  suffix="%"
                  type="number"
                  value={value}
                />
              </BlockStack>
            </Card>
            <InlineStack align="end">
              <ButtonGroup>
                <Button disabled={fetching} onClick={handleDiscard}>
                  {t("discard")}
                </Button>
                <Button disabled={!discountCode || !value} loading={fetching} onClick={handleSave} variant="primary">
                  {t("save")}
                </Button>
              </ButtonGroup>
            </InlineStack>
          </BlockStack>
          <BlockStack gap={{ md: "200", xs: "400" }}>
            <PreviewCard />
          </BlockStack>
        </InlineGrid>
      )}
    </Page>
  );
};
